import React, { useContext, useRef, useState, useEffect } from 'react';
import { CartContext } from '../../../context/CartContext';
import { X, Printer, XCircle, AlertTriangle } from 'lucide-react';
import { DEFAULT_PRINTER_CONFIG, PRINT_SETTINGS } from '../../../config/printerConfig';
import printService from '../../../services/printService';
import { toast } from 'react-hot-toast';
import receiptConfigurationService from '../../../services/receiptConfigurationService';
import branchService from '../../../services/branchService';
import { generateCancelKOTContent, DEFAULT_RECEIPT_CONFIG } from '../../../utils/receiptFormatter';

export default function CancelKOTReceiptDisplay({ onClose, kotData, cancelReason }) {
  const { getCurrentOrder, currentTableId, currentSplitLabel } = useContext(CartContext);

  const receiptRef = useRef(null);
  const currentOrder = getCurrentOrder();
  const currentTable = kotData?.table;

  // Receipt configuration state
  const [receiptConfig, setReceiptConfig] = useState(DEFAULT_RECEIPT_CONFIG);
  // Branch (restaurant) details state
  const [branchDetails, setBranchDetails] = useState(null);

  // Fetch default receipt configuration and branch details
  useEffect(() => {
    const fetchReceiptConfig = async () => {
      try {
        const config = await receiptConfigurationService.getDefault('CANCEL_KOT');
        if (config) {
          setReceiptConfig(config);
        }
      } catch (error) {
        console.warn('Failed to fetch Cancel KOT receipt configuration, using defaults:', error);
      }
    };

    const fetchBranchDetails = async () => {
      try {
        const branch = await branchService.getCurrentBranch();
        if (branch) {
          setBranchDetails(branchService.formatBranchForReceipt(branch));
        }
      } catch (error) {
        console.warn('Failed to fetch branch details:', error);
      }
    };

    fetchReceiptConfig();
    fetchBranchDetails();
  }, []);

  // Group items by station - use kitchenStationName directly from API
  const groupItemsByStation = (items) => {
    const grouped = {};

    items.forEach(item => {
      // Use kitchenStationName directly from API (no conversion!)
      // Default to 'MAIN KITCHEN' only if null/undefined
      const stationName = item.kitchenStationName || 'MAIN KITCHEN';

      if (!grouped[stationName]) {
        grouped[stationName] = [];
      }
      grouped[stationName].push(item);
    });

    return grouped;
  };

  const stationGroups = groupItemsByStation(kotData?.items || []);
  const stations = Object.keys(stationGroups);

  const timestamp = new Date().toLocaleString('en-IN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  // 🖨️ Use printer configuration from config file
  const PRINTER_CONFIG = DEFAULT_PRINTER_CONFIG;

  /**
   * 🖨️ Direct Print to Thermal Printers (via backend)
   * Prints cancel KOT to each station's printer
   * Uses receipt configuration from database for formatting
   */
  const handleDirectPrint = async () => {
    try {
      // Print to each station's printer
      const printPromises = stations.map(async (station) => {
        const stationItems = stationGroups[station];

        // Generate formatted content using receipt configuration and branch details
        const cancelKotData = {
          restaurantName: branchDetails?.name || 'MAVERICK DINE',
          kotNumber: kotData?.kotNumber || 'N/A',
          tableName: kotData?.table?.name || currentTable?.name || 'N/A',
          tableSplit: currentSplitLabel,
          pax: currentOrder?.pax,
          stationName: station,
          timestamp: timestamp,
          items: stationItems.map(item => ({
            name: item.name,
            quantity: item.quantity,
            customization: item.customization,
          })),
          cancelReason: cancelReason,
        };

        // Generate formatted content based on receipt configuration
        // generateCancelKOTContent returns { content, fontSize, kotNumber, station }
        const cancelKotResult = generateCancelKOTContent(receiptConfig, cancelKotData);
        const formattedContent = cancelKotResult.content;

        // Send formatted content to printer via backend
        return printService.testPrintTemplate(formattedContent, 'CANCEL_KOT', null);
      });

      const results = await Promise.all(printPromises);

      // Check if all prints succeeded
      const allSuccess = results.every(r => r?.success);
      const failedStations = results
        .filter(r => !r?.success)
        .map((r, idx) => stations[idx]);

      if (allSuccess) {
        toast.success('All Cancel KOTs printed successfully!', { duration: 2000 });
        return true;
      } else if (failedStations.length > 0) {
        toast.error(`Cancel KOT print failed for: ${failedStations.join(', ')}. Using browser print.`, { duration: 3000 });
        return false;
      }
      return true;
    } catch (error) {
      console.error('Direct Cancel KOT print error:', error);
      return false;
    }
  };

  // 🖨️ Enhanced Print Cancel Receipt for Thermal Printers
  const handlePrint = async () => {
    // Try direct print first
    const directPrintSuccess = await handleDirectPrint();
    if (directPrintSuccess) {
      // Close modal after successful direct print
      setTimeout(() => onClose(), 300);
      return;
    }

    // Fall back to browser print if direct print fails
    handleBrowserPrint();
  };

  // 🖨️ Browser-based print (fallback)
  const handleBrowserPrint = () => {
    // Generate HTML for each station cancel receipt
    const receiptsHTML = stations.map((station) => {
      const items = stationGroups[station];

      return `
        <div class="kot-receipt" style="page-break-after: always;">
          <!-- Receipt Header -->
          <div class="header">
            <div class="company-name">${branchDetails?.name || 'MAVERICK DINE'}</div>

            <!-- CANCEL BADGE - PROMINENT -->
            <div class="cancel-badge-container">
              <div class="cancel-badge">❌ CANCELLED ❌</div>
            </div>

            <!-- Station Badge -->
            <div class="station-badge-container">
              <span class="station-badge">${station}</span>
            </div>

            <div class="receipt-number">#${kotData?.kotNumber || 'N/A'}</div>
          </div>

          <!-- Table and Time Info -->
          <div class="info-section cancel-info">
            <div class="info-row">
              <span class="font-bold">Table:</span>
              <span class="font-bold">${kotData?.table?.name || currentTable?.name || 'N/A'}${currentSplitLabel ? ` (${currentSplitLabel})` : ''}</span>
            </div>
            ${currentOrder?.pax > 0 ? `
            <div class="info-row">
              <span>Guests:</span>
              <span>${currentOrder.pax}</span>
            </div>
            ` : ''}
            <div class="info-row text-xs">
              <span>Cancelled:</span>
              <span>${timestamp}</span>
            </div>
          </div>

          <!-- CANCEL REASON BOX -->
          <div class="cancel-reason-box">
            <div class="reason-label">⚠️ CANCELLATION REASON:</div>
            <div class="reason-text">${cancelReason || 'No reason provided'}</div>
          </div>

          <!-- Items to Cancel -->
          <div class="items-header cancel-items-header">
            <span>❌ Items to CANCEL</span>
            <span>Qty</span>
          </div>

          ${items.map(item => `
            <div class="item-row">
              <div class="item-main">
                <span class="item-name">${item.name}</span>
                <span class="item-qty">${item.quantity}</span>
              </div>
              ${item.customization ? `
                <div class="customization-details">
                  ${item.customization.size ? `<div>Size: ${item.customization.size}</div>` : ''}
                  ${item.customization.spiceLevel ? `<div>Spice: ${item.customization.spiceLevel}</div>` : ''}
                  ${item.customization.preparation ? `<div>Prep: ${item.customization.preparation}</div>` : ''}
                  ${item.customization.quickNotes?.length > 0 ? `<div>Notes: ${item.customization.quickNotes.join(', ')}</div>` : ''}
                  ${item.customization.customInstructions ? `
                    <div class="custom-instructions">⚠️ ${item.customization.customInstructions}</div>
                  ` : ''}
                </div>
              ` : ''}
            </div>
          `).join('')}

          <!-- Action Notice -->
          <div class="action-notice">
            <div class="notice-title">🚨 DO NOT PREPARE THESE ITEMS 🚨</div>
            <div class="notice-text">If already prepared, please discard</div>
          </div>

          <!-- Footer -->
          <div class="footer cancel-footer">
            <div class="text-xs">
              Total Items Cancelled: <span class="font-bold cancel-count">${items.reduce((sum, item) => sum + item.quantity, 0)}</span>
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Open print window with enhanced thermal printer CSS
    const printWindow = window.open('', '', 'width=800,height=600');

    // Check if popup was blocked
    if (!printWindow) {
      toast.error('Popup blocked! Please allow popups for this site, then try again.');
      return;
    }

    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>CANCEL KOT ${kotData?.kotNumber || 'N/A'} - ${currentTable?.name || ''}</title>
          <style>
            /* Reset and Base Styles */
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            /* Screen Preview Styles */
            @media screen {
              body {
                font-family: 'Courier New', Courier, monospace;
                background: #f5f5f5;
                padding: 20px;
              }

              .kot-receipt {
                background: white;
                width: ${PRINTER_CONFIG.width};
                max-width: 100%;
                padding: ${PRINTER_CONFIG.padding};
                margin-bottom: 20px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                border: 3px solid #dc2626;
              }
            }

            /* Print Styles for Thermal Printer */
            @media print {
              @page {
                size: ${PRINTER_CONFIG.width} auto;
                margin: 0;
              }

              body {
                margin: 0;
                padding: 0;
                width: ${PRINTER_CONFIG.width};
                background: white;
              }

              .kot-receipt {
                width: 100%;
                padding: ${PRINTER_CONFIG.padding};
                ${PRINT_SETTINGS.enableAutoCut ? `padding-bottom: ${PRINT_SETTINGS.bottomMargin};` : ''}
                page-break-after: always;
              }

              .kot-receipt:last-child {
                page-break-after: auto;
              }

              /* Prevent page breaks inside items */
              .item-row {
                page-break-inside: avoid;
              }
            }

            /* Common Styles */
            body {
              font-family: 'Courier New', Courier, monospace;
              font-size: ${PRINTER_CONFIG.fontSize};
              line-height: ${PRINTER_CONFIG.lineHeight};
              color: #000;
            }

            /* Typography */
            .text-center { text-align: center; }
            .text-xs { font-size: 0.8em; }
            .font-bold { font-weight: bold; }

            /* Header */
            .header {
              text-align: center;
              margin-bottom: 1em;
              padding-bottom: 1em;
              border-bottom: 2px dashed #dc2626;
            }

            .company-name {
              font-size: 1.4em;
              font-weight: bold;
              margin-bottom: 0.5em;
              letter-spacing: 0.05em;
            }

            .cancel-badge-container {
              margin: 1em 0;
            }

            .cancel-badge {
              background: #dc2626;
              color: #fff;
              padding: 0.75em 1.5em;
              display: inline-block;
              font-weight: bold;
              font-size: 1.2em;
              border: 3px solid #000;
            }

            .station-badge-container {
              margin: 0.75em 0;
            }

            .station-badge {
              background: #000;
              color: #fff;
              padding: 0.5em 1.5em;
              display: inline-block;
              font-weight: bold;
              font-size: 0.9em;
            }

            .receipt-number {
              font-size: 0.8em;
              margin-top: 0.5em;
            }

            /* Info Section */
            .info-section {
              margin-bottom: 1em;
              padding-bottom: 0.75em;
              border-bottom: 1px solid #dc2626;
            }

            .cancel-info {
              background: #fee2e2;
              padding: 0.5em;
              border-radius: 3px;
            }

            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.25em;
              font-size: 0.9em;
            }

            /* Cancel Reason Box */
            .cancel-reason-box {
              background: #fee2e2;
              border: 2px solid #dc2626;
              padding: 0.5em;
              margin: 0.75em 0;
              border-radius: 3px;
            }

            .reason-label {
              font-size: 0.8em;
              font-weight: bold;
              color: #7f1d1d;
              margin-bottom: 0.25em;
              text-transform: uppercase;
            }

            .reason-text {
              font-size: 0.9em;
              color: #7f1d1d;
              font-weight: bold;
            }

            /* Items */
            .items-header {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              margin-bottom: 0.5em;
              padding-bottom: 0.5em;
              border-bottom: 1px solid #dc2626;
              font-size: 0.9em;
            }

            .cancel-items-header {
              background: #fee2e2;
              padding: 0.25em 0.5em;
            }

            .items-header span:nth-child(1) { flex: 1; }
            .items-header span:nth-child(2) { width: 3em; text-align: center; }

            .item-row {
              margin-bottom: 0.75em;
            }

            .item-main {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 0.2em;
            }

            .item-name {
              flex: 1;
              font-weight: bold;
            }

            .item-qty {
              width: 3em;
              text-align: center;
              font-weight: bold;
            }

            .customization-details {
              font-size: 0.8em;
              color: #333;
              margin-left: 0.5em;
              margin-top: 0.25em;
            }

            .customization-details div {
              margin-bottom: 0.15em;
            }

            .custom-instructions {
              font-weight: bold;
              color: #b45309;
              margin-top: 0.25em;
            }

            /* Action Notice */
            .action-notice {
              background: #fee2e2;
              border: 2px solid #dc2626;
              padding: 0.5em;
              margin: 0.75em 0;
              border-radius: 3px;
              text-align: center;
            }

            .notice-title {
              font-size: 0.8em;
              font-weight: bold;
              color: #7f1d1d;
            }

            .notice-text {
              font-size: 0.8em;
              color: #7f1d1d;
              margin-top: 0.25em;
            }

            /* Footer */
            .footer {
              border-top: 2px dashed #dc2626;
              padding-top: 0.75em;
              margin-top: 1em;
              text-align: center;
            }

            .cancel-count {
              color: #dc2626;
            }
          </style>
        </head>
        <body>
          ${receiptsHTML}

          <script>
            // Auto-print on load
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, ${PRINT_SETTINGS.autoPrintDelay});
            };

            // Close window after print or cancel
            ${PRINT_SETTINGS.autoCloseAfterPrint ? `
            window.onafterprint = function() {
              setTimeout(function() {
                window.close();
              }, ${PRINT_SETTINGS.autoCloseDelay});
            };
            ` : ''}
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(printHTML);
    printWindow.document.close();

    // Close modal after a short delay to ensure print window opens
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header - RED for Cancel */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-3 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-2">
            <XCircle size={18} />
            <h3 className="text-base font-bold">⚠️ CANCEL Kitchen Order Tickets</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition">
            <X size={20} />
          </button>
        </div>

        {/* Alert Banner */}
        <div className="bg-red-50 border-b-2 border-red-200 px-4 py-2 flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-600" />
          <p className="text-xs font-semibold text-red-900">
            These receipts will notify kitchen staff to CANCEL and DISCARD this order
          </p>
        </div>

        {/* Receipts Container - Full Width */}
        <div className="flex-1 p-4 bg-slate-100 overflow-y-auto">
          <div ref={receiptRef} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stations.map((station, idx) => {
              const items = stationGroups[station];

              return (
                <div key={idx} className="bg-white p-4 rounded-lg font-mono text-sm border-4 border-red-500 shadow-md">
                  {/* Receipt Header */}
                  <div className="text-center border-b-2 border-dashed border-red-400 pb-4 mb-4">
                    {receiptConfig.showRestaurantName && (
                      <h1 className="text-xl font-bold text-slate-900">{branchDetails?.name || 'MAVERICK DINE'}</h1>
                    )}

                    {/* ⭐ CANCEL BADGE - PROMINENT */}
                    <div className="my-4">
                      <div className="bg-red-600 text-white px-6 py-3 text-xl font-black inline-block border-4 border-red-800 shadow-lg">
                        ❌ CANCELLED ❌
                      </div>
                    </div>

                    {/* Station Badge */}
                    {receiptConfig.showKitchenStation && (
                      <div className="my-3">
                        <span className="bg-slate-900 text-white px-4 py-2 text-sm font-bold inline-block">
                          {station}
                        </span>
                      </div>
                    )}

                    {receiptConfig.showOrderNumber && (
                      <p className="text-xs text-slate-600 mt-2">
                        #{kotData?.kotNumber || 'N/A'}
                      </p>
                    )}
                  </div>

                  {/* Table and Time Info */}
                  <div className="border-b border-red-300 pb-3 mb-3 bg-red-50 p-2 rounded">
                    {receiptConfig.showTableNumber && (
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-semibold text-slate-900">Table:</span>
                        <span className="font-bold text-slate-900">
                          {kotData?.table?.name || currentTable?.name || 'N/A'}
                          {currentSplitLabel && ` (${currentSplitLabel})`}
                        </span>
                      </div>
                    )}
                    {currentOrder?.pax > 0 && (
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-semibold text-slate-900">Guests:</span>
                        <span className="font-bold text-slate-900">{currentOrder.pax}</span>
                      </div>
                    )}
                    {receiptConfig.showTimestamp && (
                      <div className="flex justify-between text-xs text-slate-600">
                        <span>Cancelled:</span>
                        <span>{timestamp}</span>
                      </div>
                    )}
                  </div>

                  {/* CANCEL REASON BOX */}
                  {receiptConfig.showSpecialInstructions && (
                    <div className="bg-red-50 border-2 border-red-400 rounded p-3 mb-4">
                      <div className="text-xs font-bold text-red-900 mb-2 uppercase">
                        ⚠️ CANCELLATION REASON:
                      </div>
                      <div className="text-sm text-red-900 font-semibold">
                        {cancelReason || 'No reason provided'}
                      </div>
                    </div>
                  )}

                  {/* Items to Cancel - NO strikethrough for readability */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs font-bold text-slate-900 mb-2 pb-2 border-b border-red-300 bg-red-50 px-2 py-1">
                      <span>❌ Items to CANCEL</span>
                      <span>Qty</span>
                    </div>
                    {items.map((item, itemIdx) => (
                      <div key={itemIdx} className="mb-3">
                        <div className="flex justify-between text-xs text-slate-900">
                          <div className="flex-1">
                            <div className="font-semibold">{item.name}</div>
                            {/* Customizations */}
                            {item.customization && (
                              <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                                {item.customization.size && (
                                  <div>Size: {item.customization.size}</div>
                                )}
                                {item.customization.spiceLevel && (
                                  <div>Spice: {item.customization.spiceLevel}</div>
                                )}
                                {item.customization.preparation && (
                                  <div>Prep: {item.customization.preparation}</div>
                                )}
                                {item.customization.quickNotes?.length > 0 && (
                                  <div>Notes: {item.customization.quickNotes.join(', ')}</div>
                                )}
                                {item.customization.customInstructions && (
                                  <div className="font-semibold text-amber-900 mt-1">
                                    ⚠️ {item.customization.customInstructions}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <span className="w-12 text-center font-bold">{item.quantity}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Action Notice */}
                  <div className="bg-red-100 border-2 border-red-400 rounded p-3 mb-3">
                    <div className="text-xs font-bold text-red-900 text-center">
                      🚨 DO NOT PREPARE THESE ITEMS 🚨
                    </div>
                    <div className="text-xs text-red-900 text-center mt-1">
                      If already prepared, please discard
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="border-t-2 border-dashed border-red-400 pt-3 text-center">
                    <div className="text-xs text-slate-600">
                      Total Items Cancelled: <span className="font-bold text-red-700">{items.reduce((sum, item) => sum + item.quantity, 0)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t-2 border-red-300 p-3 flex gap-2 flex-shrink-0">
          <button
            onClick={handlePrint}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition shadow-md"
          >
            <Printer size={16} />
            Print All Cancel Receipts
          </button>

          <button
            onClick={onClose}
            className="flex-1 px-5 py-2.5 bg-white border-2 border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 hover:border-slate-400 transition shadow-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}